import React, { useState, useEffect } from "react";
import "./Home.scss";
import { useNavigate, useOutletContext } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import CircularProgress from "../../utils/CircularProgress";
import PerformanceLineChart from "../../utils/PerformanceLineChart";
import {
  getDashboardChartData,
  getDashboardSummary,
  getJobList,
  getLatestNotifications
} from "../../Redux/ActionCreators/dashboardAction";
import { getSubscriptionPlans } from "../../Redux/ActionCreators/userAction";
import { getEngagements } from "../../Redux/ActionCreators/interviewResultsAction";
import { formatNotification } from "../../utils/notificationFormatter";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import moment, { min } from "moment";
import Select from "react-select"

const Dashboard = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { notificationTrigger } = useOutletContext();

  const { chartData, summaryData, jobList, notifications = [] } = useSelector((state) => state.dashboard);
  const { selectedTenant } = useSelector((state) => state.users);
  const { engagement } = useSelector((state) => state.interviewResults);

  const [activeTab, setActiveTab] = useState("telephonic");
  const [dateRange, setDateRange] = useState([moment().subtract(6, "days").toDate(),new Date(),]);
  const [startDate, endDate] = dateRange;
  const [selectedJobId, setSelectedJobId] = useState(null);
  // const [selectedEventType, setSelectedEventType] = useState("925f880e-ebd7-4b6a-af57-2eb5e950fc83");
  const [chartLoading, setChartLoading] = useState(false);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [topJobsLoading, setTopJobsLoading] = useState(false);
  const [planLoading, setPlanLoading] = useState(false);
  const [notificationsLoading, setNotificationsLoading] = useState(false);

  const tabs = [
    {key: "telephonic",label: "Telephonic Screening",icon: "/images/circle-phone.svg",bg: "#0048B7",},
    {key: "video",label: "Video Interviews",icon: "/images/video-icon.svg",bg: "#00DDFF",},
    {key: "ai",label: "AI Assessments",icon: "/images/user-check.svg",bg: "#FF1C58",},
  ];

  useEffect(() => {
    dispatch(getJobList());
  }, [dispatch]);

  useEffect(() => {
    if (notificationTrigger) {
      fetchNotifications(1);
    }
  }, [notificationTrigger]);

  const jobOptions = jobList.map((job) => ({
    value: job.id,
    label: job.title
  }));

  const handleJobChange = (selectedOption) => {
    setSelectedJobId(selectedOption ? selectedOption.value : null);
  };

  const fetchChartData = async () => {
    const payload = {
      start_date: moment(startDate).format("YYYY-MM-DD"),
      end_date: moment(endDate).format("YYYY-MM-DD"),
      // event_type: selectedEventType,
      // job_id: selectedJobId || null,
      ...(selectedJobId && { job_id: selectedJobId }),
    };
    try {
      await dispatch(getDashboardChartData(payload));
    } catch (err) {
      console.error("Error fetching chart data:", err);
    } finally {
      setChartLoading(false);
    }
  };

  const fetchsummary = async () => {
    const payload = {
      start_date: moment(startDate).format("YYYY-MM-DD"),
      end_date: moment(endDate).format("YYYY-MM-DD"),
      ...(selectedJobId && { job_id: selectedJobId }),
    };
    await dispatch(getDashboardSummary(payload));
    setSummaryLoading(false);
  };

  const fetchNotifications = async () => {
    try {
      setNotificationsLoading(true);
      await dispatch(getLatestNotifications({ page: 1, page_size: 5 }));
    } catch (err) {
      console.error("Error fetching notifications:", err);
    } finally {
      setNotificationsLoading(false);
    }
  };

  const handleSubscriptionPlan = async () => {
    const tenantId = localStorage.getItem("tenentId");
    await dispatch(getSubscriptionPlans(tenantId));
    setPlanLoading(false);
  };

  useEffect(() => {
    const runSequentiallly = async () => {
      if (startDate && endDate) {
        setChartLoading(true);
        setSummaryLoading(true);
        setNotificationsLoading(true);
        fetchChartData();
        fetchsummary();
        fetchNotifications();
      }
    };
    runSequentiallly();
  }, [startDate, endDate, selectedJobId,]);

  const categoryMap = {
      telephonic: "6ab896ce-71ef-4a23-a805-6d0fe8251153",
      video: "73397da6-063f-4937-b364-e77455c2e51b",
      ai: "1e76d23b-6715-4a8a-8bac-c40ba1c5a78c",
  };

  const fetchEngagements = async () => {
    setTopJobsLoading(true);
    const payload = {
      event_category_id: categoryMap[activeTab],
      page: 1,
      page_size: 5,
    };
    try {
      await dispatch(getEngagements(payload));
    } catch (err) {
      console.error("Error fetching engagements:", err);
    } finally {
      setTopJobsLoading(false);
    }
  };

  useEffect(() => {
    if (startDate && endDate && activeTab) {
      fetchEngagements();
    }
  }, [activeTab, startDate, endDate]);

  useEffect(() => {
    setPlanLoading(true);
    handleSubscriptionPlan();
  }, []);

  const getSummaryStats = (categoryKey) => {
    const category = summaryData?.summary?.find(
      (item) => item.category === categoryKey
    );
    if (!category) return { scheduled: 0, completed: 0, percentage: 0 };

    if (categoryKey === "Candidate") {
      const scheduled = category.Assigned || 0;
      const total = category.Created || 0;
      const percentage =
      total > 0
        ? Math.round((scheduled / total) * 100)
        : 0;
      return { scheduled, completed: total, percentage };
    }

    const scheduled = category.Scheduled || 0;
    const completed = category.Completed || 0;
    const percentage =
      scheduled > 0
        ? Math.min(100, Math.round((completed / scheduled) * 100))
        : 0;

    return { scheduled, completed, percentage };
  };

  const getFeatureUsage = (code) => {
    const usage = selectedTenant?.data?.feature_usages?.find(
      (f) => f.subscription_feature?.code === code
    );
    if (!usage) return { total: 0, used: 0, remaining: 0, percent: 0 };

    const { total_count, used_count, remaining_count } = usage;
    const percent = total_count > 0 ? (used_count / total_count) * 100 : 0;
    return {
      total: total_count,
      used: used_count,
      remaining: remaining_count,
      percent,
    };
  };

  const getOverallUsagePercentage = () => {
    const relevantCodes = ["VOICE_CALL", "VIDEO_CALL", "ASSESSMENT", "CANDIDATE"];
    const filtered = selectedTenant?.data?.feature_usages?.filter((f) =>
      relevantCodes.includes(f.subscription_feature?.code)
    );

    if (!filtered?.length) return 0;

    const totalUsed = filtered.reduce(
      (sum, item) => sum + (item.used_count || 0),
      0
    );
    const totalAvailable = filtered.reduce(
      (sum, item) => sum + (item.total_count || 0),
      0
    );

    return totalAvailable > 0
      ? Math.round((totalUsed / totalAvailable) * 100)
      : 0;
  };

  const customStyles = {
    control: (base) => ({
      ...base,
      width: '175px',
    }),
    menu: (base) => ({
      ...base,
      width: '230px',
    }),
  };

  return (
    <div className="dashboard">
      <h1>Dashboard</h1>
      <div className="performance-stats">
        <div className="performance-stats-top">
          <h4>Performance stats</h4>
          <div className="d-flex flex-wrap" style={{gap:'12px'}}>
              <Select
                options={jobOptions}
                onChange={handleJobChange}
                isClearable
                isSearchable
                placeholder="Select Job"
                className="job-select-dropdown"
                styles={customStyles}
              />
              <DatePicker
                selectsRange
                startDate={startDate}
                endDate={endDate}
                onChange={(update) => {
                  setDateRange(update);
                }}
                isClearable={true}
                dateFormat="dd MMM"
                className="form-control date-range-picker"
                placeholderText="Select date range"
              />
            </div>
        </div>
        <div className="performance-stats-head row">
          <div className="performance-stats-head-left col-lg-8 col-12">
            <div className="performance-stats-body-left d-flex align-items-center">
              {chartLoading ? (
                <div className="loading-overlay" style={{ maxHeight: "400px" }}>
                  <div className="spinner-border" role="status">
                    <span className="visually-hidden"></span>
                  </div>
                </div>
              ) : (
                <PerformanceLineChart chartData={chartData} />
              )}
            </div>
          </div>
          <div className="performance-stats-head-right col-lg-4 col-12">
            <div className="performance-stats-body"> 
              {summaryLoading ? (
                <div
                  className="performance-stats-body-right"
                  style={{
                    justifyContent: "center",
                    alignItems: "unset",
                    height: "380px",
                    width: '100%'
                  }}
                >
                  <div className="text-center py-2">
                    <div
                      className="spinner-border"
                      role="status"
                      style={{
                        width: "1.5rem",
                        height: "1.5rem",
                        color: "#f75891",
                      }}
                    >
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="performance-stats-body-right">
                  <div
                    className="performance-stats-body-items"
                    style={{ marginBottom: "40px" }}
                  >
                    <div className="performance-stats-body-processbar">
                      <h5>
                        Telephonic <br /> Screening
                      </h5>
                      <CircularProgress
                        percentage={
                          getSummaryStats("Telephonic Screening").percentage
                        }
                        strokeColor="#0048b7"
                        icon="/images/circle-phone-black.svg"
                        value={getSummaryStats("Telephonic Screening").scheduled}
                        label="Scheduled"
                      />
                      <h6 style={{ color: "#0048B7" }}>
                        {getSummaryStats("Telephonic Screening").completed}{" "}
                        Completed
                      </h6>
                    </div>
                    <div className="performance-stats-body-processbar">
                      <h5>
                        Video <br />
                        Interviews
                      </h5>
                      <CircularProgress
                        percentage={getSummaryStats("Video Interview").percentage}
                        strokeColor="#00DDFF"
                        icon="/images/video-player-black.svg"
                        value={getSummaryStats("Video Interview").scheduled}
                        label="Assigned"
                      />
                      <h6 style={{ color: "#00DDFF" }}>
                        {getSummaryStats("Video Interview").completed} Completed
                      </h6>
                    </div>
                  </div>
                  <div className="performance-stats-body-items">
                    <div className="performance-stats-body-processbar">
                      <h5>
                        Candidate <br />
                        Assessments
                      </h5>
                      <CircularProgress
                        percentage={getSummaryStats("AI Assessments").percentage}
                        strokeColor="#FF1C58"
                        icon="/images/user-check-black.svg"
                        value={getSummaryStats("AI Assessments").scheduled}
                        label="Assigned"
                      />
                      <h6 style={{ color: "#FF1C58" }}>
                        {getSummaryStats("AI Assessments").completed} Completed
                      </h6>
                    </div>
                    <div className="performance-stats-body-processbar">
                      <h5>
                        Candidate <br />
                        Engagement
                      </h5>
                      <CircularProgress
                        percentage={getSummaryStats("Candidate").percentage}
                        strokeColor="#FFC857"
                        icon="/images/person-plus-black.svg"
                        value={getSummaryStats("Candidate").scheduled}
                        label="Scheduled"
                      />
                      <h6 style={{ color: "#FFC857" }}>
                        {getSummaryStats("Candidate").completed} Engaged
                      </h6>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="live-feed-plan">
        <div className="live-feed">
          <div
            className="d-flex justify-content-between"
            style={{ marginBottom: "18px" }}
          >
            <h4>Live Feed</h4>
            <button onClick={() => navigate("/dashboard/live-feed")}>
              View all
            </button>
          </div>
          <div className="feeds">
            {notificationsLoading ? (
              <div className="text-center d-flex justify-content-center align-items-center py-4" style={{height:'316px'}}>
                <div className="spinner-border" role="status" style={{color: "#f75891"}}>
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            ) : notifications.length ? (
              notifications.map((notif) => {
                const { id, timeAgo, message, icon, bgColor, to, navState } = formatNotification(notif);

                return (
                  <div key={id} className="feed-items">
                    <div className="icon" style={{ background: bgColor }}>
                      <img src={icon} alt="" />
                    </div>
                    <div className="feed-text">
                      <p style={{ fontSize: "12px",color: '#0000008A', lineHeight:'auto' }}>{timeAgo}</p>
                      <p>{message}</p>
                    </div>
                    <div className="d-flex align-items-center" style={{ width: '10%' }}>
                      <button 
                      onClick={() => {
                        if (navState) {
                          navigate(to, { state: navState });
                        } else {
                          navigate(to);
                        }
                      }}
                      >
                        <img src="/images/circle-right-black.svg" alt="" />
                      </button>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="no-data-feed-wrapper" style={{background:'unset'}}>
                <div className="no-data-feed-boxes">
                  <div className="no-data-feed-boxes-items"></div>
                  <div className="no-data-feed-boxes-items"></div>
                  <div className="no-data-feed-boxes-items"></div>
                  <div className="no-data-feed-boxes-items"></div>
                  <div className="no-data-feed-boxes-items"></div>
                </div>
                <div className="no-data-overlay">
                  <h6>NO DATA <br />AVAILABLE</h6>
                  <span style={{top:'75%'}}>Data unavailable ! Please wait for systems to get updated with activity data</span>
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="plan-usage">
          {planLoading ? (
            <div className="loading-overlay" style={{ height: "365px", width:'21vw' }}>
              <div className="spinner-border" role="status">
                <span className="visually-hidden"></span>
              </div>
            </div>
          ) : (
            <>
              <div
                className="d-flex justify-content-between"
                style={{ marginBottom: "18px" }}
              >
                <h4>Plan usage</h4>
              </div>
              <p>Your plan expires on 20-Jul-2025</p>
              <div className="plan">
                {[
                  {
                    label: "Telephone Screening",
                    color: "#0048B7",
                    code: "VOICE_CALL",
                  },
                  {
                    label: "Video interviews",
                    color: "#00DDFF",
                    code: "VIDEO_CALL",
                  },
                  {
                    label: "Candidate Assessments",
                    color: "#FF1C58",
                    code: "ASSESSMENT",
                  },
                  {
                    label: "Candidates",
                    color: "#FFC857",
                    code: "CANDIDATE",
                  },
                ].map(({ label, color, code }) => {
                  const usage = getFeatureUsage(code);
                  return (
                    <div className="plan-item" key={code}>
                      <h5>{label}</h5>
                      <div className="plan-score">
                        <div
                          className="process-bar"
                          style={{ background: color }}
                        >
                          <div
                            className="inner-process-bar"
                            style={{ width: `${usage.percent}%` }}
                          ></div>
                        </div>
                        <h4>{usage.percent.toFixed(2)}%</h4>
                        <p>
                          {usage.used}/{usage.total}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
              <p>
                You have used <strong>{getOverallUsagePercentage()}%</strong> of
                your total available credits
              </p>
            </>
          )}
        </div>
      </div>
      <div className="engagenent">
        <div
          className="d-flex justify-content-between flex-wrap"
          style={{ marginBottom: "12px" }}
        >
          <p>Top 5 jobs engagement</p>
          <h5 style={{ width: "13vw" }}>Top 5 jobs engagement</h5>
          <h5 style={{ width: "6vw" }}>Job title</h5>
          <h5 style={{ width: "8vw" }}>Candidates added</h5>
          <h5 style={{ width: "5vw" }}>Scheduled</h5>
          <h5 style={{ width: "2vw" }}>Completed</h5>
          <h5></h5>
          <h5></h5>
        </div>
        <div className="engagement-table-top">
          <div className="engagement-tabs">
            {tabs.map((tab) => (
              <div
                key={tab.key}
                className={`tab-item ${activeTab === tab.key ? "active" : ""}`}
                onClick={() => setActiveTab(tab.key)}
              >
                <div className={`engagement-tabs-icon ${tab.key}`}>
                  <img src={tab.icon} alt="" />
                </div>
                <h6>{tab.label}</h6>
              </div>
            ))}
          </div>
          <div className="engagement-table">
            {topJobsLoading ? (
              <div className="text-center py-4">
                <div className="spinner-border" role="status" style={{width: "1.5rem", height: "1.5rem", color: "#f75891",}}>
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            ) : engagement?.data?.length ? (
              engagement.data.map((job, index) => (
                <div
                  key={job.job_id || index}
                  className="table-row d-flex justify-content-between"
                >
                  <p style={{ width: "15vw" }}>{job.job_title}</p>
                  <p style={{ width: "10vw" }}>{job.candidates_added}</p>
                  <p style={{ width: "9vw" }}>{job.scheduled}</p>
                  <p style={{ width: "4vw" }}>{job.completed}</p>
                  <p style={{ color: "#FF1C91", cursor: "pointer" }} 
                    onClick={() => {
                        const tabMapping = {
                        ai: "Assessments",
                        video: "Video Interview",
                        telephonic: "Voice interview",
                        };

                        navigate(`/jobs/${job?.job_id}/candidates`, {
                        state: { jobId: job?.id },
                        });

                        sessionStorage.setItem("selectedJobId", job?.job_id);
                    }}
                  >
                    View details
                  </p>
                </div>
              ))
            ) : (
              <div className="no-data-engagement-wrapper">
                <div className="no-data-overlay">
                  <h6>NO DATA <br />AVAILABLE</h6>
                  <span style={{top:'85%'}}>Data unavailable ! Please wait for systems to get updated with activity data</span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
